import { isMonthBeforeDay } from '@/helpers/date-helpers'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'
import { RateUnitType } from '@/constants/booking/RateUnitType'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import dayjs from '@/services/date/index'
import TimesheetTimeStringDetailsModel from '@/models/disputes/timesheetTimeStringDetailsModel'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { mapActions, mapGetters } from 'vuex'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'
// eslint-disable-next-line no-unused-vars
import DisputeDetailsResolutionView from '@/models/disputes/responses/disputeDetailsResolutionView'
import TimesheetModificationDetailsViewModel from '@/models/disputes/timesheetModificationDetailsViewModel'
import { TimesheetChangeType } from '@/constants/disputes/TimesheetChangeType'
import { DurationUnits } from '@/constants/date/DurationUnits'
import TimeRange from '@/models/general/date/timeRange'
import TimesheetModificationsViewModel from '@/models/disputes/timesheetModificationsViewModel'

export default {
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
      activeTimesheet: 'disputes/activeTimesheet',
      isLoadingDisputeSubmit: 'disputes/isLoadingDisputeSubmit',
    }),
    isTimeEntryResolve() {
      return this.resolveStatus === ResolveOperations.TIME_ENTRY
    },
  },
  methods: {
    ...mapActions({
      submitTimesheets: 'disputes/submitTimesheets',
    }),
    renderDate(dateUtc, timezone, format) {
      let formatString = format

      if (!formatString) {
        formatString = isMonthBeforeDay(this.$i18n.locale.toLowerCase())
          ? DateFormatToken.dddMMDDYYYYWithSlash
          : DateFormatToken.dddDDMMYYYYWithSlash
      }

      const dateTz = dateUtc.tz(timezone)
      return dateTz.format(formatString)
    },
    renderTime(dateUtc, timezone, format = DateFormatToken.HHmm) {
      return this.renderDate(dateUtc, timezone, format)
    },
    renderLocalDateToTime(date, format = DateFormatToken.HHmm) {
      return dayjs(date).format(format)
    },
    renderLocalTime(
      time,
      initialFormat = DateFormatToken.HHmm,
      newFormat = DateFormatToken.hmma
    ) {
      return dayjs(time, initialFormat).format(newFormat)
    },
    renderFullOrAbbreviatedTime(time, abbreviated) {
      return abbreviated ? time : this.renderLocalTime(time)
    },
    isHourlyOrHybridUnitType(timesheet) {
      const hourlyUnitTypes = [
        RateUnitType.HOURLY,
        RateUnitType.CLIENT_UNIT_CANDIDATE_HOURLY,
      ]
      return hourlyUnitTypes.includes(timesheet?.unitType)
    },
    getBreakMinutesText(minutes, abbreviated = false) {
      return minutesToHoursMinsString(minutes, abbreviated)
    },
    getOriginalBookingDetails(timesheet, abbreviated = false) {
      const timeFormatString = abbreviated
        ? DateFormatToken.HHmm
        : DateFormatToken.hmma

      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderLocalDateToTime(
          timesheet.startTimeLocal,
          timeFormatString
        ),
        endTime: this.renderLocalDateToTime(
          timesheet.endTimeLocal,
          timeFormatString
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.breakMinutes,
        abbreviated,
      })
    },
    getModifiedBookingDetails(timesheet, abbreviated = false) {
      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.startTime,
          abbreviated
        ),
        endTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.endTime,
          abbreviated
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.candidateBookingModifications.unpaidBreakMinutes,
        abbreviated,
      })
    },
    /**
     * Returns the original booking details or the modified details, if the
     * candidate has modified the booking
     * @param {*} timesheet
     * @param {Boolean} abbreviated
     * @returns
     */
    getModifiedOrOriginalBookingDetails(timesheet, abbreviated = false) {
      const hasCandidateModifiedTimesheet =
        timesheet.bookingStatus ===
        ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED

      return hasCandidateModifiedTimesheet
        ? this.getModifiedBookingDetails(timesheet, abbreviated)
        : this.getOriginalBookingDetails(timesheet, abbreviated)
    },
    /**
     * @param {TimesheetTimeStringDetailsModel} bookingDetails
     * @returns
     */
    generateFullBookingTimeString(bookingDetails) {
      let bookingTimeString = `${bookingDetails.startTime} - ${bookingDetails.endTime}`

      bookingTimeString += `\u00A0\u00A0${bookingDetails.payOptionType}`

      bookingTimeString += `\u00A0\u00A0${this.getBreakMinutesText(
        bookingDetails.breaks,
        bookingDetails.abbreviated
      )}`

      return bookingTimeString
    },
    isDifferencePositive(differenceInMinutes) {
      return differenceInMinutes > 0
    },
    differenceInMinutesAsPositiveInt(differenceInMinutes) {
      return Math.abs(differenceInMinutes)
    },
    /**
     * Checks if the time entry clock-in is after the scheduled end time (partial entry)
     * @param {CandidateTimeEntries} candidateTimeEntries
     * @param {string} scheduledEndTime - Format: HH:mm:ss
     * @returns {boolean}
     */
    isPartialTimeEntryClockInAfterScheduledEndTime(
      candidateTimeEntries,
      scheduledEndTime
    ) {
      if (!candidateTimeEntries || candidateTimeEntries.endTime) return false

      const { HHmm, HHmmss } = DateFormatToken
      const startTime = dayjs(candidateTimeEntries.startTime, HHmm)

      const closestMinuteEndTime = dayjs(scheduledEndTime, [
        HHmmss,
        HHmm,
      ]).format(HHmm)

      const endTime = dayjs(closestMinuteEndTime, HHmm)

      return startTime.isAfter(endTime)
    },

    /**
     * @param {DisputeDetailsResolutionView} timesheet
     * @returns {Array<keyof ResolveOperations>}
     */
    determineTimesheetSubmissionOrder(timesheet) {
      const submissionsList = []

      if (timesheet.candidateHasSubmitted)
        submissionsList.push({
          type: ResolveOperations.CANDIDATE,
          date: dayjs(timesheet?.candidateTimesheet?.submittedDateUtc),
        })

      if (timesheet.clientHasSubmitted)
        submissionsList.push({
          type: ResolveOperations.CLIENT,
          date: dayjs(timesheet?.clientTimesheet?.submittedDateUtc),
        })

      if (timesheet.candidateTimeEntries) {
        const tz = timesheet.location?.timeZone
        const currentDateAtLocation = dayjs().tz(tz)
        const bookingStart = dayjs(timesheet.startDate)

        submissionsList.push({
          type: ResolveOperations.TIME_ENTRY,
          date: dayjs(timesheet.candidateTimeEntries?.firstInteractionDateUtc),
          hidden:
            !timesheet.candidateTimeEntries?.endTime &&
            currentDateAtLocation.isSame(bookingStart, DurationUnits.DAY),
        })
      }

      const originalTimes = {
        type: ResolveOperations.ORIGINAL,
        date: dayjs(timesheet.startDate),
      }

      if (
        submissionsList.length === 1 &&
        this.isPartialTimeEntryClockInAfterScheduledEndTime(
          timesheet.candidateTimeEntries,
          timesheet.endTime
        )
      ) {
        submissionsList.unshift(originalTimes)
      } else {
        submissionsList.push(originalTimes)
      }

      return submissionsList
    },
    bookingStatusText(bookingStatus) {
      const translationExists = this.$te(`booking.status.${bookingStatus}`)

      if (!translationExists) return bookingStatus
      return `${this.$t(`booking.status.${bookingStatus}`)} (${bookingStatus})`
    },
    async submitActiveTimesheet() {
      if (this.resolveStatus !== null) {
        await this.submitTimesheets({
          bookingId: this.activeTimesheet.bookingId,
          operation: this.resolveStatus,
        })
      }
    },
    resetResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: null,
      })
    },
    isAcceptOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.ACCEPT
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.ACCEPT
          )
        case ResolveOperations.ORIGINAL:
        default:
          return true
      }
    },
    isCancelOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.CANCEL
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.CANCEL
          )
        case ResolveOperations.ORIGINAL:
        default:
          return false
      }
    },
    isModifiedOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.MODIFY
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.MODIFY
          )
        case ResolveOperations.ORIGINAL:
        default:
          return false
      }
    },
    retrieveModifiedTimesheetPayload(resolveStatus) {
      if (
        (!this.isModifiedOperation(resolveStatus) ||
          resolveStatus === ResolveOperations.ORIGINAL) &&
        !this.isTimeEntryResolve
      )
        return null

      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return this.activeTimesheet.candidateTimesheet.modifications
        case ResolveOperations.CLIENT:
          return this.activeTimesheet.clientTimesheet.modifications
        case ResolveOperations.TIME_ENTRY:
          return this.compileTimeEntryModifications()
        default:
          return null
      }
    },
    /**
     *
     * @returns {TimesheetModificationsViewModel}
     */
    compileTimeEntryModifications() {
      /** @type {TimesheetModificationDetailsViewModel[]} */
      const changes = []

      const addChangeIfNotNull = (value) =>
        value !== null && changes.push(value)

      /**
       * @type {DisputeDetailsResolutionView}
       */
      const {
        endTime,
        startTime,
        breakMinutes,
        candidateTimeEntries = {},
      } = this.activeTimesheet

      const {
        startTime: timeEntryStartTime,
        endTime: timeEntryEndTime,
        mealBreakMinutes: timeEntryMealBreakMinutes,
      } = candidateTimeEntries

      const [scheduledStartTime, scheduledEndTime] = [startTime, endTime].map(
        (t) => dayjs(t, DateFormatToken.HHmmss).format(DateFormatToken.HHmm)
      )

      const originalBookingTimes = new TimeRange(
        scheduledStartTime,
        scheduledEndTime
      )

      const timeEntryTimes = new TimeRange(
        timeEntryStartTime,
        timeEntryEndTime ?? scheduledEndTime
      )

      const nullableBookingDurationChange = this.analyzeTimeRangesChanges(
        originalBookingTimes,
        timeEntryTimes
      )

      addChangeIfNotNull(nullableBookingDurationChange)

      const nullableBreakDurationChange = this.compareTimeEntryBreakDurations(
        breakMinutes,
        timeEntryMealBreakMinutes
      )

      addChangeIfNotNull(nullableBreakDurationChange)

      return new TimesheetModificationsViewModel({
        startTime: timeEntryStartTime,
        endTime: timeEntryEndTime ?? scheduledEndTime,
        unpaidBreakMinutes: timeEntryMealBreakMinutes ?? breakMinutes ?? null,
        changes,
      })
    },
    /**
     * Analyzes the difference between two time ranges.
     * @param {TimeRange} range1 - The first time range.
     * @param {TimeRange} range2 - The second time range.
     * @returns {TimesheetModificationDetailsViewModel | null} Returns null if the time ranges are identical, otherwise the difference
     */
    analyzeTimeRangesChanges(range1, range2) {
      if (range1.isEqualTo(range2)) return null

      const duration1 = range1.getDurationInMinutes()
      const duration2 = range2.getDurationInMinutes()

      const type =
        duration1 === duration2
          ? TimesheetChangeType.shift
          : TimesheetChangeType.delta

      const differenceInMinutes =
        type === TimesheetChangeType.shift
          ? range2
              .getStartTime()
              .diff(range1.getStartTime(), DurationUnits.MINUTE)
          : duration2 - duration1

      return new TimesheetModificationDetailsViewModel({
        differenceInMinutes,
        type,
      })
    },
    /**
     * Compares scheduled and the recorded time entry break durations.
     * @param {number} scheduledMinutes - The number of minutes scheduled for breaks.
     * @param {number?} actualMinutes - The number of minutes recorded for breaks.
     * @returns {TimesheetModificationDetailsViewModel | null} The difference in minutes, or null if there is no change.
     */
    compareTimeEntryBreakDurations(scheduledMinutes, actualMinutes) {
      // If the time entry break mins are null, we default to using the scheduled
      // break minutes. Thus, there will be no change.
      if (actualMinutes === null) return null

      // Handles the scenario where there are no scheduled meal breaks but
      // candidate had breaks anyways. The 0 will cause the difference
      // to be the full duration of the time entry breaks.
      const normalisedScheduledBreakMins = scheduledMinutes ?? 0

      return this.compareBreakDurations(
        normalisedScheduledBreakMins,
        actualMinutes
      )
    },
    /**
     * Compares scheduled and actual break durations.
     * @param {number} scheduledMinutes - The number of minutes scheduled for breaks.
     * @param {number} actualMinutes - The number of minutes actually taken for breaks.
     * @returns {TimesheetModificationDetailsViewModel | null} The difference in minutes, or null if there is no change.
     */
    compareBreakDurations(scheduledMinutes, actualMinutes) {
      if (scheduledMinutes === actualMinutes) return null

      const differenceInMinutes = actualMinutes - scheduledMinutes

      return new TimesheetModificationDetailsViewModel({
        type: TimesheetChangeType.unpaidBreaks,
        differenceInMinutes,
      })
    },
    clientHasSubmittedTimesheet(bookingStatus) {
      return ShortTermStatus.getClientSubmittedStatuses().includes(
        bookingStatus
      )
    },
    candidateHasSubmittedTimesheet(bookingStatus) {
      return ShortTermStatus.getCandidateSubmittedStatuses().includes(
        bookingStatus
      )
    },
  },
}
